import { useState } from "react";
import { useAuth } from "@/store/AuthStore";
import BrandLogo from "@/components/layout/BrandLogo";
import type { ApprovalStatus } from "@/types";

interface PendingApprovalProps {
  status: Exclude<ApprovalStatus, "approved">;
}

type Copy = {
  title: string;
  icon: string;
  tone: "warn" | "bad";
  message: string;
  hint: string;
};

const COPY: Record<Exclude<ApprovalStatus, "approved">, Copy> = {
  pending: {
    title: "Cuenta en revisión",
    icon: "ri-time-line",
    tone: "warn",
    message:
      "Tu registro como asesor fue recibido correctamente. El administrador (Juanka) debe aprobar tu cuenta antes de que puedas entrar a tu portal de ventas.",
    hint:
      "Mientras esperas, verifica que tus datos y tu referencia personal estén completos. Serás notificado cuando tu cuenta sea aprobada.",
  },
  rejected: {
    title: "Solicitud no aprobada",
    icon: "ri-close-circle-line",
    tone: "bad",
    message:
      "Tu solicitud para unirte a la red de asesores no fue aprobada. Si crees que es un error, comunícate con el administrador (Juanka) para revisar tu caso.",
    hint:
      "El administrador puede reconsiderar tu solicitud. Vuelve a verificar o escríbele directamente para resolverlo.",
  },
  suspended: {
    title: "Acceso suspendido",
    icon: "ri-pause-circle-line",
    tone: "warn",
    message:
      "Tu acceso al portal de asesor está temporalmente suspendido por el administrador. Tus datos y tu tenencia en la red siguen intactos.",
    hint:
      "Comunícate con el administrador (Juanka) para reactivar tu acceso. No perdiste tu lugar en la red ni tu inventario en consignación.",
  },
};

// Pantalla que ve un asesor cuando su cuenta aún no está aprobada (pendiente/rechazada/suspendida).
const PendingApproval = ({ status }: PendingApprovalProps) => {
  const { signOut, refreshProfile } = useAuth();
  const [checking, setChecking] = useState(false);

  const copy = COPY[status];
  const bad = copy.tone === "bad";

  const recheck = async () => {
    setChecking(true);
    try {
      await refreshProfile();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="attd-app-bg min-h-screen w-full flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <BrandLogo />

        <div className="glass-panel-strong rounded-xl p-6 md:p-8 mt-6 border-accent-500/25 animate-fade-up text-center">
          <span
            className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              bad ? "bg-primary-500/20 text-primary-100" : "bg-accent-500/20 text-accent-200"
            }`}
          >
            <i className={`${copy.icon} text-3xl`} />
          </span>

          <h1 className="editable-title text-2xl font-extrabold tracking-tight">{copy.title}</h1>

          <p className="text-sm text-foreground-600 mt-3">{copy.message}</p>

          <div className="mt-5 rounded-md bg-accent-500/10 border border-accent-500/25 px-4 py-3 text-left">
            <p className="text-[11px] text-foreground-700 flex items-start gap-2">
              <i className="ri-lightbulb-line text-accent-300 mt-0.5" />
              <span>{copy.hint}</span>
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={recheck}
              disabled={checking}
              className="flex-1 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              {checking ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-refresh-line" />
              )}
              {checking ? "Verificando..." : "Verificar de nuevo"}
            </button>
            <button
              onClick={() => void signOut()}
              className="flex-1 py-2.5 rounded-md border border-accent-500/30 text-foreground-700 hover:text-foreground-950 hover:border-accent-500/50 text-sm font-semibold whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-logout-box-r-line" /> Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingApproval;